import React, { useState, useCallback, useMemo } from "react";
import { Camera, Eye, Sparkles, Info, Plus, Minus, FileText, Settings } from "lucide-react";
import { SuggestedModelItem } from "@/utils/messageParser/types";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useFormContext } from "@/contexts/FormContext";
import { useCentralizedVehicleInterest } from "@/hooks/useCentralizedVehicleInterest";
import { useIsMobile } from "@/hooks/use-mobile";
// import { ga4Tracker, extractVehicleData } from '@/utils/ga4Tracking'; // Not used in this file
import { openURL } from "@/utils/navigationUtils";
import YouTubeVideoViewer from "./YouTubeVideoViewer";
import { decodeText } from "@/utils/textDecoding";
import { getVehicleCardByStyleIdSync } from "@/utils/showroomUtils";
import { useDealerSession } from "@/contexts/DealerSessionContext";
import { compressButtonText } from "@/utils/textCompression";
import PaymentSelector from "./PaymentSelector";
import { PaymentPreferencesProvider } from "@/contexts/PaymentPreferencesContext";

// --- Utility Functions (Memoized/Stable) ---

// Helper to extract YouTube video ID from URL (supports watch, youtu.be, embed, shorts)
const extractYoutubeId = (url: string): string | null => {
  if (!url) return null;

  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#\/\s]+)/i,
    /youtube\.com\/watch\?.*v=([^&\n?#\/\s]+)/i,
    /youtube\.com\/shorts\/([^&\n?#\/\s]+)/i,
    /youtube\.com\/v\/([^&\n?#\/\s]+)/i,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) return match[1];
  }

  return null;
};

// Helper to get vehicle style ID
const getModelStyleId = (model: SuggestedModelItem): string | number | undefined => {
  return (model as any).showroom_styleId || (model as any).styleid || (model as any).styleId;
};

// --- Type Definitions (Re-using for component props) ---
interface ShowroomCardsProps {
  models: SuggestedModelItem[];
  paymentPreference?: {
    Type?: "Finance" | "Lease" | string;
    Term?: number;
    Frequency?: "weekly" | "biweekly" | "monthly" | string;
    kmAllowance?: number;
  };
}
// --- End Type Definitions ---

// --- ShowroomCard Component (Extracted for better rendering performance) ---

interface ShowroomCardProps {
  model: SuggestedModelItem;
  index: number;
  isExpanded: boolean;
  toggleCardExpansion: (index: number) => void;
  paymentsEnabled: boolean;
  paymentPreference: ShowroomCardsProps["paymentPreference"];
  // Handlers and Formatters passed down
  handleImageClick: (model: SuggestedModelItem) => Promise<void>;
  handleMoreClick: (model: SuggestedModelItem) => Promise<void>;
  handleSeeOfferClick: (model: SuggestedModelItem) => void;
  formatPrice: (price: number | undefined) => string;
  formatFuel: (fuel: number | string | undefined) => string;
  formatFeature: (feature: string) => string;
  formatButtonText: (buttonText: string) => string;
  getShowroomYoutubeVideos: (
    model: SuggestedModelItem,
    modelIndex: number,
  ) => Array<{ youtube_id: string; Button_Text: string }> | null;
  getPriceDisclaimer: () => string;
}

const ShowroomCard: React.FC<ShowroomCardProps> = React.memo(
  ({
    model,
    index,
    isExpanded,
    toggleCardExpansion,
    paymentsEnabled,
    paymentPreference,
    handleImageClick,
    handleMoreClick,
    handleSeeOfferClick,
    formatPrice,
    formatFuel,
    formatFeature,
    formatButtonText,
    getShowroomYoutubeVideos,
    getPriceDisclaimer,
  }) => {
    const { sessionData } = useDealerSession();
    const { language } = useLanguage();
    const isMobile = useIsMobile();
    const styleId = getModelStyleId(model);

    const hasFeatures = model.Features && model.Features.length > 0;
    const hasDescription = model.Vehicle_Description_Summary;
    const hasExpandableContent = hasFeatures || hasDescription;

    // Simple check: render PaymentSelector if payments enabled and styleId exists
    const shouldShowPaymentBanner = paymentsEnabled && !!styleId;

    const getDisplayModel = (m: SuggestedModelItem) => {
      if (!m.Model) return "";
      if (!m.Trim) return m.Model;
      const combinedLength = m.Model.length + m.Trim.length + 1;
      return combinedLength <= 12 ? `${m.Model} ${m.Trim}` : m.Model;
    };

    const shouldShowTrimSeparately = (m: SuggestedModelItem) => {
      if (!m.Model || !m.Trim) return false;
      const combinedLength = m.Model.length + m.Trim.length + 1;
      return combinedLength > 12;
    };

    // Use a simplified image error handler for cleaner JSX
    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      const target = e.target as HTMLImageElement;
      // Simple fallback to hide broken image and stop retrying
      target.style.display = "none";
      const container = target.closest(".image-container");
      if (container && !container.querySelector(".image-fallback")) {
        const fallback = document.createElement("div");
        fallback.className =
          "image-fallback w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg d2cbot-bot_border d2cbot-bot_border-gray-200 flex items-center justify-center text-gray-400 absolute inset-0";
        fallback.innerHTML = `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/></svg>`;
        container.appendChild(fallback);
      }
    };

    // Get showroom or AI-provided videos (suppressed entirely when the dealer has videos disabled)
    const videosEnabled = sessionData?.videosEnabled !== false;
    const validVideos = videosEnabled ? getShowroomYoutubeVideos(model, index) : null;

    return (
      <div
        key={index}
        className="relative w-full max-w-full overflow-hidden min-w-0 bg-white rounded-lg d2cbot-bot_border d2cbot-bot_border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow duration-200"
      >
        {/* Top Banner for Payment - PaymentSelector fetches its own data */}
        {shouldShowPaymentBanner && (
          <PaymentSelector
            styleId={styleId as string | number}
            initialPaymentType={paymentPreference?.Type as "Finance" | "Lease" | undefined}
            initialTerm={paymentPreference?.Term}
            initialKmAllowance={paymentPreference?.kmAllowance}
            frequency={(paymentPreference?.Frequency as "weekly" | "biweekly" | "monthly") || sessionData?.paymentFrequency || "monthly"}
          />
        )}

        {/* Main model info row */}
        <div className={`flex items-center mb-3 ${shouldShowPaymentBanner ? "mt-4" : ""}`}>
          {/* Column 1: Model Image - 4:3 aspect ratio - Only show if image exists */}
          {model.Main_Picture_URL && (
            <div className="flex-shrink-0 w-20 mr-4 image-container">
              <AspectRatio ratio={4 / 3}>
                <HoverCard>
                  <HoverCardTrigger asChild>
                    <div className="relative w-full h-full">
                      <img
                        src={
                          model.Main_Picture_URL.startsWith("http")
                            ? model.Main_Picture_URL
                            : `https://${model.Main_Picture_URL}`
                        }
                        alt={`${model.Year} ${model.Make} ${model.Model}`}
                        className={`w-full h-full object-cover rounded-lg d2cbot-bot_border d2cbot-bot_border-gray-200 ${model.VDP_URL ? "cursor-pointer hover:opacity-80 transition-opacity" : ""}`}
                        onClick={() => handleImageClick(model)}
                        onError={handleImageError}
                      />
                    </div>
                  </HoverCardTrigger>
                  <HoverCardContent
                    className="w-auto p-0 d2cbot-bot_border-0 bg-transparent shadow-2xl"
                    side="top"
                    align="start"
                    sideOffset={-50}
                    alignOffset={-10}
                  >
                    <div className="bg-white rounded-lg d2cbot-bot_border d2cbot-bot_border-gray-300 shadow-lg overflow-hidden p-3 flex flex-col justify-center items-center">
                      <div className="relative w-48 h-36">
                        <img
                          src={
                            model.Main_Picture_URL.startsWith("http")
                              ? model.Main_Picture_URL
                              : `https://${model.Main_Picture_URL}`
                          }
                          alt={`${model.Year} ${model.Make} ${model.Model}`}
                          className="w-full h-full object-cover rounded d2cbot-bot_border d2cbot-bot_border-gray-300 cursor-pointer"
                          onClick={() => handleImageClick(model)}
                          onError={handleImageError}
                        />
                      </div>
                      <div className="text-xs text-gray-400 text-center mt-2">
                        {language === "fr" ? "Autres coul. dispo." : "Other colors avail."}
                      </div>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              </AspectRatio>
            </div>
          )}

          {/* Column 2: Year + Make, Model, Trim */}
          <div className={`flex-grow${isMobile ? " min-w-[38%]" : ""}`}>
            <div className="text-sm font-bold text-gray-900 leading-tight">
              {model.Year} {model.Make}
            </div>
            {isMobile ? (
              <div className="text-sm text-gray-700 font-medium truncate" title={getDisplayModel(model)}>
                {getDisplayModel(model)}
              </div>
            ) : (
              <div className="text-sm text-gray-700 font-medium">{getDisplayModel(model)}</div>
            )}
            {shouldShowTrimSeparately(model) && <div className="text-xs text-gray-500">{model.Trim}</div>}
          </div>

          {/* Column 3: Fuel Badge, Price */}
          <div className={`text-right flex-shrink-0${isMobile ? " max-w-[35%]" : ""}`}>
            {model.Fuel_Type && (
              <div className="flex justify-end mb-1">
                <span
                  className={`inline-flex items-center rounded-full text-xs font-semibold px-2.5 py-0.5 border w-fit whitespace-nowrap ${
                    typeof model.Fuel_Type === "string" &&
                    (model.Fuel_Type.toLowerCase().includes("electric") ||
                      model.Fuel_Type.toLowerCase().includes("électrique"))
                      ? "bg-fuel-electric-bg text-fuel-electric border-fuel-electric"
                      : typeof model.Fuel_Type === "string" &&
                          (model.Fuel_Type.toLowerCase().includes("phev") ||
                            model.Fuel_Type.toLowerCase().includes("plugin") ||
                            model.Fuel_Type.toLowerCase().includes("rechargeable"))
                        ? "bg-fuel-phev-bg text-fuel-phev border-fuel-phev"
                        : typeof model.Fuel_Type === "string" &&
                            (model.Fuel_Type.toLowerCase().includes("hybrid") ||
                              model.Fuel_Type.toLowerCase().includes("hybride"))
                          ? "bg-fuel-hybrid-bg text-fuel-hybrid border-fuel-hybrid"
                          : "bg-fuel-gas-bg text-fuel-gas border-fuel-gas"
                  }`}
                >
                  {formatFuel(model.Fuel_Type)}
                </span>
              </div>
            )}
            <div className="flex flex-col items-end gap-1">
              <div className="text-sm font-bold text-black flex items-center justify-end gap-1">
                {formatPrice(model.Price)}
                {getPriceDisclaimer() && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3 h-3 cursor-help text-gray-500" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{getPriceDisclaimer()}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons and Expand/Collapse */}
        <div className="space-y-2">
          <div className="flex flex-wrap gap-y-2 justify-between items-center">
            {/* More/Less button */}
            {hasExpandableContent ? (
              <button
                onClick={() => toggleCardExpansion(index)}
                className="d2cbot-btn-auto flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800 transition-colors"
              >
                {isExpanded ? (
                  <>
                    <Minus className="w-3 h-3" />
                    {language === "fr" ? "Moins" : "Less"}
                  </>
                ) : (
                  <>
                    <Plus className="w-3 h-3" />
                    {language === "fr" ? "Plus" : "More"}
                  </>
                )}
              </button>
            ) : (
              <div></div> // Empty div to maintain flex layout
            )}

            {/* Action buttons on the right side */}
            <div className="flex flex-wrap justify-end gap-1.5 gap-y-2">
              {/* Brochure button - temporarily disabled
              {(() => {
                const vehicleCard = styleId ? getVehicleCardByStyleIdSync(styleId) : null;
                const brochureLink = vehicleCard
                  ? language === "fr"
                    ? vehicleCard.vehicleBrochureFrench
                    : vehicleCard.vehicleBrochureEnglish
                  : null;
                const finalLink = brochureLink || model.Brochure?.Link;

                if (!finalLink) return null;

                return (
                  <Button
                    onClick={async () => {
                      await handleMoreClick(model); // Re-uses interest store and openURL
                      await openURL(finalLink);
                    }}
                    variant="outline"
                    size="sm"
                    className="!bg-black d2cbot-bot_border-black !text-white hover:!bg-gray-700 hover:!text-white px-1.5 py-0.5 text-xs rounded-md transition-colors duration-200 h-7 flex items-center gap-1"
                  >
                    <FileText className="w-2.5 h-2.5" />
                    {model.Brochure?.Button_Text || (language === "fr" ? "Brochure" : "Brochure")}
                  </Button>
                );
              })()}
              */}

              <Button
                onClick={() => handleMoreClick(model)}
                variant="outline"
                size="sm"
                className="!bg-black d2cbot-bot_border-black !text-white hover:!bg-gray-700 hover:!text-white px-1.5 py-0.5 text-xs rounded-md transition-colors duration-200 h-7 flex items-center gap-1"
              >
                <Eye className="w-2.5 h-2.5" />
                {language === "fr" ? "Plus" : "More"}
              </Button>

              <Button
                onClick={() => handleSeeOfferClick(model)}
                size="sm"
                className="!bg-red-600 hover:!bg-red-700 text-white hover:!text-white px-1.5 py-0.5 text-xs rounded-md transition-colors duration-200 h-7 flex items-center gap-1"
              >
                <Sparkles className="w-2.5 h-2.5" />
                {language === "fr" ? "Voir l'offre" : "See offer"}
              </Button>
            </div>
          </div>

          {/* Build & Price button - full width below action buttons */}
          {model.Build_And_Price && (
            <Button
              onClick={async () => {
                await handleMoreClick(model); // Re-uses interest store and openURL
                if (model.Build_And_Price?.Link) {
                  await openURL(model.Build_And_Price.Link);
                }
              }}
              variant="secondary"
              size="sm"
              className="w-full text-xs min-h-8 border border-black flex items-center gap-1.5 whitespace-normal py-2"
            >
              <Settings className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="break-words">
                {compressButtonText(formatButtonText(model.Build_And_Price.Button_Text), 30)}
              </span>
            </Button>
          )}

          {/* YouTube Videos - full width below Build & Price */}
          {validVideos && validVideos.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              {/* Video Section Header */}
              <div className="mb-3 px-1">
                <div
                  className="text-base font-bold text-gray-900"
                  dangerouslySetInnerHTML={{
                    __html:
                      language === "fr"
                        ? `${validVideos.length > 1 ? "Vid&eacute;os" : "Vid&eacute;o"} du ${model.Make} ${model.Model} ${model.Year}`
                        : `${model.Year} ${model.Make} ${model.Model} ${validVideos.length > 1 ? "Videos" : "Video"}`,
                  }}
                />
                <div className="h-0.5 w-12 bg-primary mt-1 rounded-full"></div>
              </div>
              <div className="space-y-2">
                {validVideos.map((video, videoIndex) => (
                  <YouTubeVideoViewer
                    key={videoIndex}
                    videoUrl={`https://www.youtube.com/watch?v=${video.youtube_id}`}
                    buttonText={decodeText(
                      video.Button_Text || (language === "fr" ? "Regarder la vidéo" : "Watch Video"),
                    )}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Expanded content */}
          {hasExpandableContent && isExpanded && (
            <div className="space-y-3 pt-2 d2cbot-bot_border-t d2cbot-bot_border-gray-100">
              {hasDescription && (
                <div className="mb-3">
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">
                    {language === "fr" ? "Description" : "Description"}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 p-3 rounded-lg">
                    {model.Vehicle_Description_Summary}
                  </p>
                </div>
              )}

              {hasFeatures && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2">
                    {language === "fr" ? "Points saillants" : "Highlights"}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {model.Features!.map((feature, featureIndex) => (
                      <span
                        key={featureIndex}
                        className="inline-flex items-center rounded-full text-xs font-semibold px-3 py-1 bg-blue-50 text-blue-800 border border-blue-200 whitespace-nowrap"
                      >
                        {formatFeature(feature)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
);

ShowroomCard.displayName = "ShowroomCard";

// --- Main ShowroomCards Component ---

const ShowroomCards: React.FC<ShowroomCardsProps> = ({ models, paymentPreference }) => {
  const { language, t } = useLanguage();
  const { activateOfferForm, updateFormData, aiFormData } = useFormContext();
  const { storeVehicleInterest } = useCentralizedVehicleInterest();
  const { sessionData } = useDealerSession();
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());

  // Use a stable Set object for tracking videos
  const usedVideoIds = useMemo(() => new Set<string>(), []);

  // Check if payments are enabled - PaymentSelector fetches its own data per-card
  const paymentsEnabled = sessionData?.paymentsEnabled === true;

  // --- Memoized Handlers and Formatters ---

  const getPriceDisclaimer = useCallback(() => {
    return localStorage.getItem("Price_Disclaimer") || "";
  }, []);

  const storeModelAsInterest = useCallback(
    (model: SuggestedModelItem) => {
      // ... (rest of the storeVehicleInterest logic is fine, omitted for brevity)
      storeVehicleInterest(model);
    },
    [storeVehicleInterest],
  );

  const getLocalizedVdpUrl = useCallback((url: string): string => {
    if (language === 'fr') {
      let localized = url;
      localized = localized.replace(/\/new\/inventory\//gi, '/neufs/inventaire/');
      localized = localized.replace(/\/used\//gi, '/occasion/');
      localized = localized.replace(/\/new\//gi, '/neuf/');
      return localized;
    }
    return url;
  }, [language]);

  const handleImageClick = useCallback(
    async (model: SuggestedModelItem) => {
      storeModelAsInterest(model);
      if (model.VDP_URL) {
        await openURL(getLocalizedVdpUrl(model.VDP_URL));
      }
    },
    [storeModelAsInterest, getLocalizedVdpUrl],
  );

  const handleMoreClick = useCallback(
    async (model: SuggestedModelItem) => {
      storeModelAsInterest(model);
      if (model.VDP_URL) {
        await openURL(getLocalizedVdpUrl(model.VDP_URL));
      }
    },
    [storeModelAsInterest, getLocalizedVdpUrl],
  );

  const handleSeeOfferClick = useCallback(
    (model: SuggestedModelItem) => {
      storeModelAsInterest(model);

      if (!aiFormData?.Title) {
        const vehicleDescription = `${model.Year || ""} ${model.Make || ""} ${model.Model || ""}`.trim();
        const offerTitle =
          language === "fr"
            ? `Obtenez notre meilleure offre sur le ${vehicleDescription}`
            : `Get our best offer on the ${vehicleDescription}`;

        updateFormData({ FormTitle: offerTitle });
      }
      activateOfferForm();
    },
    [storeModelAsInterest, language, aiFormData?.Title, activateOfferForm, updateFormData],
  );

  const formatPrice = useCallback(
    (price: number | undefined) => {
      if (!price || price === 0) return language === "fr" ? "n/d" : "n/a";
      if (language === "fr") {
        return `${price.toLocaleString("fr-CA")} $`;
      } else {
        return `$${price.toLocaleString("en-CA")}`;
      }
    },
    [language],
  );

  const formatFeature = useCallback(
    (feature: string): string => {
      if (!feature) return feature;
      const lowerFeature = feature.toLowerCase().trim();

      // Check for full drivetrain terms first
      if (lowerFeature.includes("all wheel drive") || lowerFeature.includes("toutes roues motrices")) {
        return language === "fr" ? "AWD (TI)" : "AWD";
      }
      if (lowerFeature.includes("front wheel drive") || lowerFeature.includes("traction avant")) {
        return language === "fr" ? "FWD (TAV)" : "FWD";
      }
      if (lowerFeature.includes("rear wheel drive") || lowerFeature.includes("propulsion")) {
        return language === "fr" ? "RWD (TAR)" : "RWD";
      }
      if (lowerFeature.includes("4 wheel drive") || lowerFeature.includes("four wheel drive") || lowerFeature.includes("quatre roues motrices")) {
        return language === "fr" ? "4WD (4RM)" : "4WD";
      }

      // Check for abbreviations
      if (lowerFeature === "awd" || lowerFeature.includes(" awd") || lowerFeature.startsWith("awd ")) {
        return language === "fr" ? "AWD (TI)" : "AWD";
      }
      if (lowerFeature === "fwd" || lowerFeature.includes(" fwd") || lowerFeature.startsWith("fwd ")) {
        return language === "fr" ? "FWD (TAV)" : "FWD";
      }
      if (lowerFeature === "rwd" || lowerFeature.includes(" rwd") || lowerFeature.startsWith("rwd ")) {
        return language === "fr" ? "RWD (TAR)" : "RWD";
      }
      if (lowerFeature === "4wd" || lowerFeature.includes(" 4wd") || lowerFeature.startsWith("4wd ")) {
        return language === "fr" ? "4WD (4RM)" : "4WD";
      }

      return feature;
    },
    [language],
  );

  const formatFuel = useCallback(
    (fuel: number | string | undefined) => {
      if (!fuel && fuel !== 0) return "N/A";
      if (typeof fuel === "string") {
        const formatted = formatFeature(fuel);
        if (formatted !== fuel) return formatted;
        if (
          fuel.toLowerCase().includes("plugin") ||
          fuel.toLowerCase().includes("phev") ||
          fuel.toLowerCase().includes("rechargeable")
        )
          return "PHEV";
        return fuel;
      }
      return `${fuel}L/100km`;
    },
    [formatFeature],
  );

  const formatButtonText = useCallback((buttonText: string) => {
    return buttonText.replace(/Build\s*(&|and)\s*Price/gi, "Build your");
  }, []);

  const getShowroomYoutubeVideos = useCallback(
    (model: SuggestedModelItem, modelIndex: number) => {
      const styleId = getModelStyleId(model);
      if (!styleId) return null;

      const vehicleCard = getVehicleCardByStyleIdSync(styleId);
      if (!vehicleCard) return null;

      const youtubeUrlsRaw =
        language === "fr" ? vehicleCard.showroomYoutubeUrlFrench : vehicleCard.showroomYoutubeUrlEnglish;

      const youtubeUrls = Array.isArray(youtubeUrlsRaw)
        ? youtubeUrlsRaw
        : typeof youtubeUrlsRaw === "string"
          ? [youtubeUrlsRaw]
          : [];

      const cleanedYoutubeUrls = youtubeUrls
        .filter((u): u is string => typeof u === "string" && u.trim().length > 0)
        .map((u) => u.trim());

      if (cleanedYoutubeUrls.length === 0) return null;

      // Find the first unused video
      for (const url of cleanedYoutubeUrls) {
        const videoId = extractYoutubeId(url);
        if (videoId && !usedVideoIds.has(videoId)) {
          usedVideoIds.add(videoId); // Mark this video as used
          return [
            {
              youtube_id: videoId,
              Button_Text: language === "fr" ? "Regarder la vidéo" : "Watch Video",
            },
          ];
        }
      }

      // Fallback to AI response videos if showroom videos are all used up
      const youtubeVideos = Array.isArray(model.Youtube_Video)
        ? model.Youtube_Video
        : model.Youtube_Video
          ? [model.Youtube_Video]
          : [];
      const validVideos = youtubeVideos.filter(
        (video): video is { youtube_id: string; Button_Text: string } => !!video?.youtube_id && !!video?.Button_Text,
      );

      return validVideos.length > 0 ? validVideos : null;
    },
    [language, usedVideoIds],
  ); // usedVideoIds is stable via useMemo

  const toggleCardExpansion = useCallback((index: number) => {
    setExpandedCards((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(index)) {
        newExpanded.delete(index);
      } else {
        newExpanded.add(index);
      }
      return newExpanded;
    });
  }, []);

  // --- Render ---

  return (
    <PaymentPreferencesProvider>
      <div className="w-full max-w-full space-y-3 mb-6 overflow-hidden min-w-0">
        {models.map((model, index) => (
          <ShowroomCard
            key={index}
            model={model}
            index={index}
            isExpanded={expandedCards.has(index)}
            toggleCardExpansion={toggleCardExpansion}
            paymentsEnabled={paymentsEnabled}
            paymentPreference={paymentPreference}
            // Pass down memoized handlers and formatters
            handleImageClick={handleImageClick}
            handleMoreClick={handleMoreClick}
            handleSeeOfferClick={handleSeeOfferClick}
            formatPrice={formatPrice}
            formatFuel={formatFuel}
            formatFeature={formatFeature}
            formatButtonText={formatButtonText}
            getShowroomYoutubeVideos={getShowroomYoutubeVideos}
            getPriceDisclaimer={getPriceDisclaimer}
          />
        ))}
      </div>
    </PaymentPreferencesProvider>
  );
};

export default ShowroomCards;
